<template>
  <div class="ledger-page">
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('ledger_journals_title') }}
          </h3>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.status"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="JOURNAL_STATUSES"
            :reduce="val => val.value"
            :placeholder="$t('ledger_status')"
            :label="localeLabel"
            clearable
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.book"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="JOURNAL_BOOKS"
            :reduce="val => val.value"
            :placeholder="$t('ledger_book')"
            :label="localeLabel"
            clearable
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <b-form-input
            v-model="filter.date_from"
            type="date"
            :placeholder="$t('ledger_date')"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <b-form-input
            v-model="filter.date_to"
            type="date"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-card class="rounded-lg">
      <div class="d-none d-md-flex justify-content-end mb-1">
        <b-button
          v-if="hasPermission('create journal_entries')"
          variant="primary"
          class="rounded-pill"
          @click="goNew"
        >
          {{ $t('ledger_journal_new') }}
        </b-button>
      </div>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!rows.length"
        class="text-center py-4"
      >
        <p class="text-muted mb-1">
          {{ $t('ledger_empty_journals') }}
        </p>
        <b-button
          v-if="hasPermission('create journal_entries')"
          variant="primary"
          class="rounded-pill"
          @click="goNew"
        >
          {{ $t('ledger_journal_new') }}
        </b-button>
      </div>

      <div v-else>
      <div class="d-none d-md-block">
        <b-table
          :items="rows"
          :fields="fields"
          hover
          small
          responsive
          class="ledger-dense-table"
        >
          <template #cell(entry_number)="data">
            <b-link :to="{ name: 'ledger-journals-view', params: { id: data.item.id } }">
              {{ data.item.entry_number }}
            </b-link>
            <late-posting-badge
              class="ml-50"
              :late="!!data.item.is_late_posting"
              :late-from-date="data.item.late_from_date"
            />
          </template>
          <template #cell(book)="data">
            {{ enumLabel(data.item.book, $i18n.locale) }}
          </template>
          <template #cell(status)="data">
            <b-badge :variant="statusBadgeVariant(enumValue(data.item.status))">
              {{ enumLabel(data.item.status, $i18n.locale) }}
            </b-badge>
          </template>
          <template #cell(debit)="data">
            <span class="ledger-num">{{ money((data.item.totals && data.item.totals.debit) || 0) }}</span>
          </template>
          <template #cell(credit)="data">
            <span class="ledger-num">{{ money((data.item.totals && data.item.totals.credit) || 0) }}</span>
          </template>
          <template #cell(actions)="data">
            <b-link :to="{ name: 'ledger-journals-view', params: { id: data.item.id } }">
              <feather-icon
                icon="EyeIcon"
                size="16"
              />
            </b-link>
          </template>
        </b-table>
      </div>

      <div class="d-md-none">
        <div
          v-for="item in rows"
          :key="item.id"
          class="ledger-journal-card"
          @click="$router.push({ name: 'ledger-journals-view', params: { id: item.id } })"
        >
          <div class="d-flex justify-content-between">
            <strong>{{ item.entry_number }}</strong>
            <span>
              <late-posting-badge
                :late="!!item.is_late_posting"
                :late-from-date="item.late_from_date"
              />
              <b-badge :variant="statusBadgeVariant(enumValue(item.status))">
                {{ enumLabel(item.status, $i18n.locale) }}
              </b-badge>
            </span>
          </div>
          <div class="text-muted small">
            {{ item.entry_date }} · {{ enumLabel(item.book, $i18n.locale) }}
          </div>
          <div>{{ item.description }}</div>
          <div class="d-flex justify-content-between mt-50 ledger-num">
            <span>{{ $t('ledger_debit') }} {{ money((item.totals && item.totals.debit) || 0) }}</span>
            <span>{{ $t('ledger_credit') }} {{ money((item.totals && item.totals.credit) || 0) }}</span>
          </div>
        </div>
      </div>
      </div>

      <b-pagination
        v-if="meta.total"
        v-model="filter.page"
        class="mt-1"
        align="center"
        :total-rows="meta.total"
        :per-page="meta.per_page || 15"
        @change="onPageChange"
      />
    </b-card>

    <b-button
      v-if="hasPermission('create journal_entries')"
      class="ledger-fab d-md-none"
      variant="primary"
      @click="goNew"
    >
      {{ $t('ledger_journal_new') }}
    </b-button>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BLink,
  BPagination, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getJournalEntries } from '@/api/accounting/journalEntries'
import LatePostingBadge from './components/LatePostingBadge.vue'
import {
  listFrom, enumValue, enumLabel, JOURNAL_BOOKS, JOURNAL_STATUSES,
  statusBadgeVariant, formatLedgerMoney, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'JournalEntriesList',
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BLink, BPagination, BSpinner, vSelect, FeatherIcon, LatePostingBadge,
  },
  data() {
    return {
      JOURNAL_BOOKS,
      JOURNAL_STATUSES,
      loading: false,
      rows: [],
      meta: {},
      filter: {
        status: this.$route.query.status || null,
        book: this.$route.query.book || null,
        date_from: this.$route.query.date_from || '',
        date_to: this.$route.query.date_to || '',
        per_page: 15,
        page: 1,
      },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    localeLabel() {
      return this.$i18n.locale === 'en' ? 'label' : 'label_ar'
    },
    fields() {
      return [
        { key: 'entry_number', label: this.$t('ledger_entry_number') },
        { key: 'entry_date', label: this.$t('ledger_date') },
        { key: 'book', label: this.$t('ledger_book') },
        { key: 'description', label: this.$t('ledger_description') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'debit', label: this.$t('ledger_debit') },
        { key: 'credit', label: this.$t('ledger_credit') },
        { key: 'actions', label: this.$t('ledger_actions') },
      ]
    },
  },
  watch: {
    'filter.status': 'reloadFirstPage',
    'filter.book': 'reloadFirstPage',
    'filter.date_from': 'reloadFirstPage',
    'filter.date_to': 'reloadFirstPage',
  },
  mounted() {
    this.loadJournals()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value, this.$i18n.locale)
    },
    goNew() {
      this.$router.push({ name: 'ledger-journals-new' })
    },
    reloadFirstPage() {
      this.filter.page = 1
      this.loadJournals()
    },
    onPageChange(page) {
      this.filter.page = page
      this.loadJournals()
    },
    async loadJournals() {
      this.loading = true
      try {
        const params = { per_page: this.filter.per_page, page: this.filter.page }
        if (this.filter.status) params.status = this.filter.status
        if (this.filter.book) params.book = this.filter.book
        if (this.filter.date_from) params.date_from = this.filter.date_from
        if (this.filter.date_to) params.date_to = this.filter.date_to
        const res = await getJournalEntries(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-journal-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
}
.ledger-fab {
  position: fixed;
  bottom: 1.25rem;
  inset-inline-end: 1rem;
  z-index: 20;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}
</style>
